/**
 * The battle's red screen-edge washes, drawn by the post chain's final pass (render/postfx.ts) instead of as DOM layers
 * (M44): the fortress's hit flash and the last stand's vignette with its heartbeat pulse.
 *
 * They used to be three full-screen `<div>`s with radial-gradient backgrounds over the WebGL canvas (hud.css
 * `.hurt-flash`, `.last-stand`, `.last-stand .beat`). Once the fortress fell into its last stand the vignette stayed on
 * for the rest of the wave — a full-screen translucent layer the compositor blended over the canvas every frame, the
 * heartbeat's layer re-rasterised on every beat (~1.5 a second) and the hit flash's on every hit. In the headless
 * battle rig the last stand alone cost 20–25 % of the frames (56 → 41–45 fps, raster tasks 6 → 25 a second); hiding the
 * vignette gave them back. In the final pass the same washes are a few multiply-adds per pixel of a pass that already
 * runs, with nothing to rasterise and no extra layer.
 *
 * The look is the CSS's, reproduced exactly (the gradients' stops and colours, the layer opacities, the 0.6 s ease-out
 * transition, the beat's keyframes and the flash's fade, composited in display space like the page did). Time is the
 * page's clock, as the Web Animations' was. Pure state and arithmetic: `uniforms(now)` is unit-tested in Node.
 */

/** CSS `ease-out` = cubic-bezier(0, 0, 0.58, 1): the eased progress at `x` in 0..1. */
export function easeOut(x: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  // solve bx(t) = x for t (x1 = 0, x2 = 0.58), then y(t) (y1 = 0, y2 = 1)
  const bx = (t: number) => 3 * (1 - t) * t * t * 0.58 + t * t * t;
  let lo = 0, hi = 1, t = x;
  for (let i = 0; i < 24; i++) { t = (lo + hi) / 2; if (bx(t) < x) lo = t; else hi = t; }
  return 3 * (1 - t) * t * t + t * t * t;
}

/** Piecewise-linear keyframes [offset, value] at progress p (offsets ascending, 0 and 1 included). */
function keyframes(k: readonly (readonly [number, number])[], p: number): number {
  for (let i = 1; i < k.length; i++) {
    const [o1, v1] = k[i];
    if (p <= o1) { const [o0, v0] = k[i - 1]; return v0 + (v1 - v0) * ((p - o0) / Math.max(1e-6, o1 - o0)); }
  }
  return k[k.length - 1][1];
}

export const SCREEN_TINT = {
  /** `.last-stand` layer: opacity 0.6 + 0.4·depth while on, eased over `standFadeMs`. */
  standFadeMs: 600,
  /** The heartbeat pulse (hudPills BeatKeys / BeatMs before M44). */
  beatMs: 560,
  beatKeys: [[0, 0], [0.12, 1], [0.3, 0.35], [0.4, 0.75], [1, 0]] as const,
  /** The hit flash (hudPills HurtMs): 0.85 → 0 over `hurtMs`, ease-out. */
  hurtMs: 520,
  hurtPeak: 0.85,
} as const;

/** What the final pass needs this frame: the stand layer's opacity and depth, the beat's and the hit flash's opacity. */
export interface TintUniforms { stand: number; depth: number; beat: number; hurt: number }

export class ScreenTintState {
  private depth = 0;
  private on = false;
  /** The stand layer's opacity transition: from `fromO` at `fromT` towards the target. */
  private fromO = 0;
  private fromT = -Infinity;
  private target = 0;
  private beatAt = -Infinity;
  private hurtAt = -Infinity;
  private readonly out: TintUniforms = { stand: 0, depth: 0, beat: 0, hurt: 0 };

  /** The last stand's depth 0..1, or null for none (HudPills.setLastStand). */
  setLastStand(depth: number | null, now: number): void {
    const on = depth !== null;
    if (on) this.depth = Math.max(0, Math.min(1, depth));
    const target = on ? 0.6 + 0.4 * this.depth : 0;
    this.on = on;
    if (Math.abs(target - this.target) < 1e-4) return;
    this.fromO = this.standOpacity(now);
    this.fromT = now;
    this.target = target;
  }
  /** One heartbeat on the vignette (only while it is on, as the CSS pulse layer lived inside it). */
  beat(now: number): void { if (this.on) this.beatAt = now; }
  /** The fortress took a hit. */
  hurt(now: number): void { this.hurtAt = now; }
  /** Everything off at once (the level goes; the hub shares the rig). */
  reset(): void { this.on = false; this.depth = 0; this.fromO = 0; this.target = 0; this.fromT = -Infinity; this.beatAt = this.hurtAt = -Infinity; }

  private standOpacity(now: number): number {
    const k = (now - this.fromT) / SCREEN_TINT.standFadeMs;
    return k >= 1 ? this.target : this.fromO + (this.target - this.fromO) * easeOut(k);
  }

  /** This frame's values (the same object each call). All zero is the normal state: the pass skips the washes. */
  uniforms(now: number): TintUniforms {
    const o = this.out;
    o.stand = this.standOpacity(now);
    o.depth = this.depth;
    const b = (now - this.beatAt) / SCREEN_TINT.beatMs;
    o.beat = b >= 0 && b < 1 ? keyframes(SCREEN_TINT.beatKeys, easeOut(b)) : 0;
    const h = (now - this.hurtAt) / SCREEN_TINT.hurtMs;
    o.hurt = h >= 0 && h < 1 ? SCREEN_TINT.hurtPeak * (1 - easeOut(h)) : 0;
    return o;
  }
}

/** The one battle's washes (the main rig's final pass reads it; the reward chest's transparent pass does not). */
export const screenTint = new ScreenTintState();
